/**
 * Interview calendar. Pulls all interview_invite + offer + follow_up signals
 * for the current applicant, extracts a probable date from the email subject
 * or body, and renders both a month grid and an upcoming agenda list.
 *
 * Date extraction is heuristic — we try common patterns (Mon Jun 2, 6/2,
 * 6/2/2026, 2026-06-02, "Tuesday at 2pm" with no date inferred). For signals
 * without a parseable date, we use the received_at as a fallback.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar as CalIcon, Loader2 } from 'lucide-react';
import { api } from '@/services/api';
import { Card } from '@/components/ui/Card';
import { useApplicants } from '@/contexts/ApplicantContext';
import type { NotificationSignal } from '@/services/types';
import clsx from 'clsx';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function extractDate(s: NotificationSignal, fallback: Date): Date {
  const text = `${s.raw_subject ?? ''} ${s.raw_snippet ?? ''}`;
  const isoMatch = text.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const d = new Date(`${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}T12:00:00Z`);
    if (!Number.isNaN(d.getTime())) return d;
  }
  const usMatch = text.match(/\b(0?[1-9]|1[0-2])\/(0?[1-9]|[12]\d|3[01])(?:\/(\d{2,4}))?\b/);
  if (usMatch) {
    const m = +usMatch[1];
    const day = +usMatch[2];
    const y = usMatch[3] ? (usMatch[3].length === 2 ? 2000 + +usMatch[3] : +usMatch[3]) : new Date().getFullYear();
    const d = new Date(Date.UTC(y, m - 1, day, 12));
    if (!Number.isNaN(d.getTime())) return d;
  }
  // "Jun 2" / "June 2nd"
  for (let mi = 0; mi < 12; mi++) {
    const re = new RegExp(`\\b${MONTHS[mi]}\\.?|\\b${MONTHS[mi].slice(0, 3)}\\.?`, 'i');
    if (re.test(text)) {
      const dayMatch = text.match(new RegExp(`${MONTHS[mi].slice(0, 3)}\\.?\\s*(\\d{1,2})`, 'i'));
      if (dayMatch) {
        const day = +dayMatch[1];
        const y = new Date().getFullYear();
        const d = new Date(Date.UTC(y, mi, day, 12));
        if (!Number.isNaN(d.getTime())) return d;
      }
    }
  }
  return fallback;
}

export default function CalendarPage() {
  const { current } = useApplicants();
  const [items, setItems] = useState<NotificationSignal[]>([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState(() => new Date());

  useEffect(() => {
    setLoading(true);
    api
      .get<{ items: NotificationSignal[] }>(`/notifications?applicant_id=${current}&limit=200&include_dismissed=1`)
      .then((r) => setItems(r.items))
      .finally(() => setLoading(false));
  }, [current]);

  const events = useMemo(() => {
    return items
      .filter((s) => s.kind === 'interview_invite' || s.kind === 'offer' || s.kind === 'follow_up')
      .map((s) => {
        const fallback = new Date(s.received_at ?? s.created_at);
        const when = extractDate(s, fallback);
        return { s, when };
      });
  }, [items]);

  const eventsByDay = useMemo(() => {
    const m = new Map<string, { s: NotificationSignal; when: Date }[]>();
    for (const e of events) {
      const key = e.when.toISOString().slice(0, 10);
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(e);
    }
    return m;
  }, [events]);

  const upcoming = useMemo(() => {
    const now = Date.now();
    return events
      .filter((e) => e.when.getTime() >= now - 86400_000)
      .sort((a, b) => a.when.getTime() - b.when.getTime())
      .slice(0, 20);
  }, [events]);

  const year = cursor.getUTCFullYear();
  const month = cursor.getUTCMonth();
  const firstDay = new Date(Date.UTC(year, month, 1));
  const firstWeekday = firstDay.getUTCDay();
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const cells: { date: Date | null; key: string }[] = [];
  for (let i = 0; i < firstWeekday; i++) cells.push({ date: null, key: `empty-${i}` });
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(Date.UTC(year, month, d));
    cells.push({ date, key: date.toISOString().slice(0, 10) });
  }

  const today = new Date().toISOString().slice(0, 10);
  const KIND_TONE: Record<string, string> = {
    interview_invite: 'bg-primary-100 text-primary-800 dark:bg-primary-500/20 dark:text-primary-100',
    offer: 'bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-300',
    follow_up: 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300',
  };

  return (
    <div className="space-y-5">
      <header className="flex items-center justify-between">
        <h2 className="text-xl font-semibold tracking-tight">
          <CalIcon className="mr-1 inline h-4 w-4 text-primary-600" />
          Interview calendar — {current}
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCursor(new Date(Date.UTC(year, month - 1, 1)))}
            className="btn-secondary"
            title="Previous month"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="w-36 text-center text-sm font-medium">
            {MONTHS[month]} {year}
          </span>
          <button
            onClick={() => setCursor(new Date(Date.UTC(year, month + 1, 1)))}
            className="btn-secondary"
            title="Next month"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <button onClick={() => setCursor(new Date())} className="btn-secondary text-xs">
            Today
          </button>
        </div>
      </header>

      {loading && <Loader2 className="h-5 w-5 animate-spin text-gray-400" />}

      <Card>
        <div className="grid grid-cols-7 gap-px overflow-hidden rounded-md bg-gray-200 dark:bg-gray-800 text-xs">
          {WEEKDAYS.map((w) => (
            <div key={w} className="bg-gray-50 px-2 py-1 text-center font-medium text-gray-600 dark:bg-gray-900 dark:text-gray-300">
              {w}
            </div>
          ))}
          {cells.map((c) => {
            if (!c.date) {
              return <div key={c.key} className="bg-white dark:bg-gray-900" />;
            }
            const key = c.date.toISOString().slice(0, 10);
            const isToday = key === today;
            const todays = eventsByDay.get(key) ?? [];
            return (
              <div
                key={c.key}
                className={clsx(
                  'min-h-[90px] bg-white p-1 align-top dark:bg-gray-900',
                  isToday && 'ring-2 ring-inset ring-primary-500',
                )}
              >
                <div className={clsx('mb-1 text-right text-xs', isToday ? 'font-bold text-primary-700' : 'text-gray-500')}>
                  {c.date.getUTCDate()}
                </div>
                <ul className="space-y-0.5">
                  {todays.slice(0, 3).map(({ s }) => (
                    <li key={s.id}>
                      <Link
                        to={s.job_id ? `/applications/${s.job_id}` : '/notifications'}
                        className={clsx(
                          'block truncate rounded px-1 py-0.5 text-[10px]',
                          KIND_TONE[s.kind ?? ''] ?? 'bg-gray-100 text-gray-700',
                        )}
                        title={s.raw_subject ?? ''}
                      >
                        {s.company ?? 'event'}
                      </Link>
                    </li>
                  ))}
                  {todays.length > 3 && (
                    <li className="text-[10px] text-gray-500">+{todays.length - 3} more</li>
                  )}
                </ul>
              </div>
            );
          })}
        </div>
      </Card>

      <Card title="Upcoming">
        {upcoming.length === 0 ? (
          <p className="text-sm text-gray-500">No upcoming events.</p>
        ) : (
          <ul className="space-y-1.5">
            {upcoming.map(({ s, when }) => (
              <li key={s.id} className="flex items-center justify-between text-sm">
                <span className="text-gray-500">
                  {when.toLocaleDateString(undefined, { month: 'short', day: '2-digit', weekday: 'short' })}
                </span>
                <span className={clsx('badge', KIND_TONE[s.kind ?? ''] ?? 'badge-gray')}>{s.kind}</span>
                <Link
                  to={s.job_id ? `/applications/${s.job_id}` : '/notifications'}
                  className="flex-1 truncate text-gray-700 hover:underline dark:text-gray-200"
                >
                  {s.company ?? '—'} — {s.raw_subject}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
